/**
 * voice-v8 — Werkzeugkarten: Körper und Lernen: Whoop-Erholung, Vokabeln, Human Design.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { aktionsKnopf, aktionsLeiste, baueKarte, fehlerBlock, kartenTitel } from "../karte.js";
import { renderMarkdown } from "../markdown.js";
import { unwrapResult } from "./hilfe.js";

// --- Card-Helpers --------------------------------------------------------

// 2026-07-22 (Martin, "wie es WHOOP auch hat"): Vital-Karte mit Ringen.
interface WhoopTag {
  datum?: string | null;
  erholung_pct?: number | null;
  hrv_ms?: number | null;
  ruhepuls?: number | null;
  schlaf?: {
    dauer_h?: number | null;
    leistung_pct?: number | null;
    effizienz_pct?: number | null;
  } | null;
  tagesbelastung?: number | null;
}

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * 2026-07-27 (Audit-Punkt 4): Wird jetzt AUCH von der Verfassungs-Seite
 * benutzt (verfassung-view.ts) — deshalb exportiert. Vorher lag der Ring
 * ungenutzt neben der Seite, die ihn am noetigsten hatte.
 * Die Farbklassen decken sich mit ampel(): gut | mittel | niedrig | neutral.
 */
export function whoopRing(
  anteil: number | null,
  farbKlasse: string,
  wert: string,
  label: string,
): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = `tw-ring tw-ring--${farbKlasse}`;
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 64 64");
  svg.setAttribute("aria-hidden", "true");
  const bg = document.createElementNS(SVG_NS, "circle");
  bg.setAttribute("cx", "32");
  bg.setAttribute("cy", "32");
  bg.setAttribute("r", "26");
  bg.setAttribute("class", "tw-ring-bg");
  svg.appendChild(bg);
  if (anteil != null && isFinite(anteil)) {
    const C = 2 * Math.PI * 26;
    const a = Math.max(0.02, Math.min(1, anteil));
    const fg = document.createElementNS(SVG_NS, "circle");
    fg.setAttribute("cx", "32");
    fg.setAttribute("cy", "32");
    fg.setAttribute("r", "26");
    fg.setAttribute("class", "tw-ring-fg");
    fg.setAttribute("stroke-dasharray", `${(C * a).toFixed(1)} ${C.toFixed(1)}`);
    svg.appendChild(fg);
  }
  const mitte = document.createElement("span");
  mitte.className = "tw-ring-wert";
  mitte.textContent = wert;
  const lbl = document.createElement("span");
  lbl.className = "tw-ring-label";
  lbl.textContent = label;
  wrap.appendChild(svg);
  wrap.appendChild(mitte);
  wrap.appendChild(lbl);
  return wrap;
}

function erholungsKlasse(pct: number | null | undefined): string {
  if (pct == null) return "neutral";
  if (pct >= 67) return "gut";
  if (pct >= 34) return "mittel";
  return "niedrig";
}

// Ticket #466 (2026-07-23): Spanisch-Trainer-Karten. vocab_due zeigt die
// Woerter OHNE Uebersetzung (kein Spoiler — der Bot fragt konversationell
// ab und bucht via vocab_result).
export function buildVocabCard(tool: string, result: unknown): HTMLElement {
  const r = unwrapResult(result) as {
    ok?: boolean;
    fehler?: string;
    neu?: boolean;
    vokabel?: {
      word?: string;
      translation?: string;
      intervalDays?: number;
      correctCount?: number;
      wrongCount?: number;
    };
    faellig?: Array<{ word?: string }>;
    anzahl?: number;
    total?: number;
    faelligJetzt?: number;
    heuteGeuebt?: number;
    heuteRichtig?: number;
    streakTage?: number;
  };
  const el = baueKarte("vocab");
  el.appendChild(kartenTitel("Spanisch"));
  if (r.ok !== true) {
    const p = document.createElement("p");
    p.className = "tw-hinweis";
    p.textContent = r.fehler || "Gerade nicht möglich.";
    el.appendChild(p);
    return el;
  }
  const zeile = (text: string, cls = "tw-vocab-zeile"): HTMLElement => {
    const p = document.createElement("p");
    p.className = cls;
    p.textContent = text;
    return p;
  };
  if (tool === "vocab_add" && r.vokabel) {
    el.appendChild(
      zeile(
        `${r.neu ? "Neu gesichert" : "Aktualisiert"}: ${r.vokabel.word} → ${r.vokabel.translation}`,
      ),
    );
    return el;
  }
  if (tool === "vocab_due") {
    const liste = r.faellig ?? [];
    if (liste.length === 0) {
      el.appendChild(zeile("Nichts fällig — alles im Rhythmus."));
      return el;
    }
    el.appendChild(zeile(`${liste.length} Vokabel${liste.length === 1 ? "" : "n"} fällig:`));
    const ul = document.createElement("ul");
    ul.className = "tw-vocab-liste";
    for (const v of liste) {
      const li = document.createElement("li");
      li.textContent = v.word ?? "";
      ul.appendChild(li);
    }
    el.appendChild(ul);
    return el;
  }
  if (tool === "vocab_result" && r.vokabel) {
    const iv = r.vokabel.intervalDays ?? 1;
    el.appendChild(
      zeile(
        `${r.vokabel.word}: nächste Wiederholung in ${iv} Tag${iv === 1 ? "" : "en"} · ${r.vokabel.correctCount ?? 0}× richtig, ${r.vokabel.wrongCount ?? 0}× daneben`,
      ),
    );
    return el;
  }
  // vocab_stats
  const meta = document.createElement("div");
  meta.className = "tw-meta";
  const teil = (wert: string, lbl: string): HTMLElement => {
    const sp = document.createElement("span");
    const b = document.createElement("b");
    b.textContent = wert;
    sp.appendChild(b);
    sp.appendChild(document.createTextNode(` ${lbl}`));
    return sp;
  };
  meta.appendChild(teil(String(r.total ?? 0), "Vokabeln"));
  meta.appendChild(teil(String(r.faelligJetzt ?? 0), "fällig"));
  meta.appendChild(
    teil(`${r.heuteRichtig ?? 0}/${r.heuteGeuebt ?? 0}`, "heute richtig"),
  );
  if ((r.streakTage ?? 0) > 0) {
    meta.appendChild(teil(String(r.streakTage), "Tage in Folge"));
  }
  el.appendChild(meta);
  return el;
}

export function buildWhoopCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as {
    ok?: boolean;
    error?: string;
    tage?: WhoopTag[];
    workouts?: Array<{
      sport?: string | null;
      dauer_min?: number | null;
      belastung?: number | null;
      puls?: number | null;
      kcal?: number | null;
    }>;
  };
  const el = baueKarte("whoop");
  el.appendChild(kartenTitel("Deine Verfassung"));
  const tag = Array.isArray(r.tage) ? r.tage[0] : undefined;
  if (r.ok !== true || !tag) {
    const p = document.createElement("p");
    p.className = "tw-hinweis";
    p.textContent =
      r.error || "Gerade keine WHOOP-Daten — nach der nächsten Synchronisierung erscheinen sie hier.";
    el.appendChild(p);
    return el;
  }

  const ringe = document.createElement("div");
  ringe.className = "tw-rings";
  const erh = tag.erholung_pct ?? null;
  ringe.appendChild(
    whoopRing(
      erh != null ? erh / 100 : null,
      erholungsKlasse(erh),
      erh != null ? `${Math.round(erh)}%` : "—",
      "Erholung",
    ),
  );
  const schlafH = tag.schlaf?.dauer_h ?? null;
  const schlafPct = tag.schlaf?.leistung_pct ?? tag.schlaf?.effizienz_pct ?? null;
  ringe.appendChild(
    whoopRing(
      schlafPct != null ? schlafPct / 100 : null,
      "schlaf",
      schlafH != null ? `${schlafH.toFixed(1).replace(".", ",")} h` : "—",
      "Schlaf",
    ),
  );
  const strain = tag.tagesbelastung ?? null;
  ringe.appendChild(
    whoopRing(
      strain != null ? strain / 21 : null,
      "belastung",
      strain != null ? strain.toFixed(1).replace(".", ",") : "—",
      "Belastung",
    ),
  );
  el.appendChild(ringe);

  const meta = document.createElement("div");
  meta.className = "tw-meta";
  const teil = (wert: string, lbl: string): HTMLElement => {
    const sp = document.createElement("span");
    const b = document.createElement("b");
    b.textContent = wert;
    sp.appendChild(b);
    sp.appendChild(document.createTextNode(` ${lbl}`));
    return sp;
  };
  if (tag.hrv_ms != null) meta.appendChild(teil(`${Math.round(tag.hrv_ms)} ms`, "HRV"));
  if (tag.ruhepuls != null) meta.appendChild(teil(`${Math.round(tag.ruhepuls)}`, "Ruhepuls"));
  if (tag.datum) meta.appendChild(teil(tag.datum.split("-").reverse().join("."), ""));
  if (meta.childNodes.length > 0) el.appendChild(meta);

  // Letzte Trainings (2026-07-22, "WHOOP voll ausschoepfen").
  const wos = (r.workouts ?? []).filter((w) => w.sport || w.belastung != null);
  if (wos.length > 0) {
    const liste = document.createElement("div");
    liste.className = "tw-workouts";
    for (const w of wos.slice(0, 2)) {
      const zeile = document.createElement("div");
      zeile.className = "tw-workout";
      const teile: string[] = [];
      if (w.sport) teile.push(w.sport);
      if (w.dauer_min != null) teile.push(`${w.dauer_min} min`);
      if (w.belastung != null)
        teile.push(`Belastung ${String(w.belastung).replace(".", ",")}`);
      if (w.puls != null) teile.push(`${w.puls} Puls`);
      if (w.kcal != null) teile.push(`${w.kcal} kcal`);
      zeile.textContent = teile.join(" · ");
      liste.appendChild(zeile);
    }
    el.appendChild(liste);
  }
  return el;
}

// Pre.805 (#78): human_design — strukturierte Karte statt Roh-Textblock.
// Wesenskern als Badges, Deutung als gerendertes Markdown (scrollbar),
// Zeitpunkt der Auswertung (#79) + Sprung in die Vollansicht.
interface HdCardShape {
  ok?: boolean;
  error?: string;
  generatedAt?: string;
  chart?: {
    type?: string;
    strategy?: string;
    authority?: string;
    profile?: string;
  };
  interpretation?: string;
}

export function buildHumanDesignCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as HdCardShape;
  const el = baueKarte("human-design");
  el.appendChild(kartenTitel("Human Design"));
  if (r.ok === false) {
    el.appendChild(
      fehlerBlock(r.error ?? "Die Auswertung konnte nicht erstellt werden."),
    );
    return el;
  }
  const c = r.chart ?? {};
  const badges = document.createElement("div");
  badges.className = "tool-ticket-badges";
  for (const v of [c.type, c.profile, c.authority]) {
    if (!v) continue;
    const b = document.createElement("span");
    b.className = "tickets-badge";
    b.textContent = v;
    badges.appendChild(b);
  }
  if (badges.childElementCount > 0) el.appendChild(badges);

  if (r.interpretation) {
    const interp = document.createElement("div");
    interp.className = "tool-hd-interp";
    renderMarkdown(interp, r.interpretation);
    el.appendChild(interp);
  }

  if (r.generatedAt) {
    const d = new Date(r.generatedAt);
    if (!isNaN(d.getTime())) {
      const when = document.createElement("div");
      when.className = "tool-hd-meta";
      when.textContent = `Auswertung erstellt am ${d.toLocaleString("de-AT", { dateStyle: "medium", timeStyle: "short" })}`;
      el.appendChild(when);
    }
  }

  aktionsLeiste(el).appendChild(
    aktionsKnopf({
      text: "Vollansicht öffnen",
      primaer: true,
      onClick: () => {
        location.hash = "#human-design";
      },
    }),
  );
  return el;
}
